import { Dialog, DialogBody, DialogFooter, DialogHeader } from '@gravity-ui/uikit';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { VideoInfo } from '../../../../types/video/VideoInfo';
import { VideosService } from '../../../../api/api-services/videos-service/videosService';
import { AvailableVideoPropertiesToEdit } from '../../../../types/video/availableVideoPropertiesToPatch';
import { toastError, toastSuccess } from '../../../../libs/toasts/toasts';
import { MAX_VIDEO_TITLE_LENGTH } from '../../../../constants/MAX_VIDEO_TITLE_LENGTH';
import { ProfilesRecord } from '../../../Page/types/ProfilesRecord';
import { VideoInfoForm } from '../../../VideoInfoForm/VideoInfoForm';
import { MobileVideoInfoForm } from '../../../MobilePage/components/MobileVideoInfoForm';
import { vzhikLogger } from '../../../../libs/vzhik-logger/VzhikLogger';
import { commonT, dialogsT, toastsT } from '../../../../i18n';
import { ThumbnailChange } from '../../../ThumbnailUpload/ThumbnailUpload';
import axios from 'axios';

type EditVideoDialogProps = {
    isOpen: boolean;
    videoInfo: VideoInfo;
    profiles: ProfilesRecord;
    isMobile?: boolean;
    onClose: () => void;
    onEdit: () => void;
};

export const EditVideoDialog: FC<EditVideoDialogProps> = ({
    isOpen,
    videoInfo,
    profiles,
    isMobile,
    onClose,
    onEdit,
}) => {
    const buttonWidth = isMobile ? 'auto' : 'max';

    const [loading, setLoading] = useState<boolean>(false);
    const [editedVideoInfo, setEditedVideoInfo] = useState<VideoInfo>({ ...videoInfo });
    const [currentThumbnailId, setCurrentThumbnailId] = useState<string | undefined>(undefined);
    const [thumbnailChange, setThumbnailChange] = useState<ThumbnailChange>({ action: 'none' });

    useEffect(() => {
        setEditedVideoInfo({ ...videoInfo });
        setThumbnailChange({ action: 'none' });
        if (videoInfo.video_id) {
            VideosService.listThumbnails(videoInfo.video_id)
                .then((response) => {
                    setCurrentThumbnailId(response.current_thumbnail_id || undefined);
                })
                .catch((error) => {
                    console.error('Failed to load thumbnails:', error);
                });
        }
    }, [videoInfo]);

    const isTitleEdited = videoInfo.title !== editedVideoInfo.title;
    const isProfileEdited = videoInfo.profile !== editedVideoInfo.profile;
    const isSummarizationEdited =
        videoInfo.summarization_enabled !== editedVideoInfo.summarization_enabled;
    const isThumbnailEdited = thumbnailChange.action !== 'none';

    const editFile = useCallback(async () => {
        try {
            setLoading(true);

            let newThumbnailId: string | undefined;

            if (thumbnailChange.action === 'upload' && thumbnailChange.file) {
                const { thumbnail_id, upload_url } = await VideosService.createThumbnail(
                    editedVideoInfo.video_id,
                );

                await axios.put(upload_url, thumbnailChange.file, {
                    headers: {
                        'Content-Type': thumbnailChange.file.type,
                    },
                });

                newThumbnailId = thumbnail_id;
            } else if (thumbnailChange.action === 'delete') {
                await VideosService.deleteCurrentThumbnail(editedVideoInfo.video_id);
            }

            const payload: AvailableVideoPropertiesToEdit = {};

            if (isTitleEdited) {
                payload.title = editedVideoInfo.title;
            }
            if (isProfileEdited) {
                payload.profile = editedVideoInfo.profile;
            }
            if (isSummarizationEdited) {
                payload.enable_summarization = editedVideoInfo.summarization_enabled;
            }
            if (newThumbnailId) {
                payload.thumbnail_id = newThumbnailId;
            }

            if (Object.keys(payload).length > 0) {
                await VideosService.editVideoDocument(editedVideoInfo.video_id, payload);
            }

            onEdit();
            onClose();
            toastSuccess.open(toastsT('fileEdited'));
        } catch (e) {
            console.error('Не удалось отредактировать видео:', e);
            toastError.open(toastsT('editFailed'));
            vzhikLogger.logError({
                message: 'Не удалось отредактировать файл',
                type: 'server',
                additional: {
                    error: e,
                },
            });
        } finally {
            setLoading(false);
        }
    }, [editedVideoInfo, thumbnailChange, isTitleEdited, isProfileEdited, isSummarizationEdited]);

    return (
        <Dialog
            className={
                isMobile
                    ? 'page-videolist-dialog page-videolist-dialog--mobile'
                    : 'page-videolist-dialog'
            }
            onClose={onClose}
            open={isOpen}
            size={isMobile ? 's' : undefined}
        >
            <DialogHeader caption={dialogsT('editVideoTitle')} id="app-confirmation-dialog-title" />
            <DialogBody>
                {isMobile ? (
                    <MobileVideoInfoForm
                        profile={editedVideoInfo.profile}
                        profiles={profiles}
                        name={editedVideoInfo.title}
                        enableSummarization={editedVideoInfo.summarization_enabled}
                        onChangeName={(title) => setEditedVideoInfo({ ...editedVideoInfo, title })}
                        onChangeProfile={(profile) =>
                            setEditedVideoInfo({ ...editedVideoInfo, profile })
                        }
                        onToggleSummarization={() =>
                            setEditedVideoInfo({
                                ...editedVideoInfo,
                                summarization_enabled: !editedVideoInfo.summarization_enabled,
                            })
                        }
                        disabled={loading}
                        thumbnailProps={{
                            currentThumbnailId,
                            onChange: setThumbnailChange,
                        }}
                    />
                ) : (
                    <VideoInfoForm
                        profile={editedVideoInfo.profile}
                        profiles={profiles}
                        name={editedVideoInfo.title}
                        enableSummarization={editedVideoInfo.summarization_enabled}
                        isCompact
                        onChangeName={(title) => setEditedVideoInfo({ ...editedVideoInfo, title })}
                        onChangeProfile={(profile) =>
                            setEditedVideoInfo({ ...editedVideoInfo, profile })
                        }
                        onToggleSummarization={() =>
                            setEditedVideoInfo({
                                ...editedVideoInfo,
                                summarization_enabled: !editedVideoInfo.summarization_enabled,
                            })
                        }
                        disabled={loading}
                        thumbnailProps={{
                            currentThumbnailId,
                            onChange: setThumbnailChange,
                        }}
                    />
                )}
            </DialogBody>
            <DialogFooter
                onClickButtonApply={editFile}
                onClickButtonCancel={onClose}
                textButtonApply={commonT('editAction')}
                textButtonCancel={commonT('cancel')}
                propsButtonApply={{
                    disabled:
                        (!isTitleEdited &&
                            !isProfileEdited &&
                            !isSummarizationEdited &&
                            !isThumbnailEdited) ||
                        !editedVideoInfo.title.length ||
                        editedVideoInfo.title.length > MAX_VIDEO_TITLE_LENGTH,
                    loading: loading,
                    width: buttonWidth,
                }}
                propsButtonCancel={{
                    disabled: loading,
                    width: buttonWidth,
                }}
            />
        </Dialog>
    );
};
